import React from 'react'
import families from '../data/productFamilies';
import Cookies from 'js-cookie';

import { axiosGet, axiosPost } from '../../common/js/axios-utility'
import { fetchLogin } from '../../common/js/bfx-customer-utility';
import { type SessionData } from '../../common/types/common.types';
import { type SerialOption } from '../../common/types/webshop.types';
import { type ProductFamily } from '../../common/types/webshop.types';

const PurchaseContext = React.createContext(null);

const login = fetchLogin();

type Props = {
    appType: string,
    useDeeplinks?: boolean,
    skipUpsell?: boolean,
    isLegacy?: boolean,
    children?: React.JSX.Element|React.JSX.Element[],
    collection?: string,
    defaultProduct?: string,
    defaultHost?: string,
    defaultType?: string,
    defaultAkey?: string
}

type PurchaseContextState = {
    loading: boolean,
    family: string,
    store: string,
    useDeeplinks: boolean,
    skipUpsell: boolean,
    appType: string,
    login: SessionData,
    defaults: any,
    productHtml: { __html: string },
    bannerUrl?: string,
    serials: Record<string, SerialOption>,
    promoUrl?: string
}

const PurchaseProvider = (props: Props) => {
    const {
        appType,
        useDeeplinks=false,
        skipUpsell=false,
        isLegacy=false
    } = props;

    const [state, setState] = React.useState<PurchaseContextState>({
        loading: true,
        family: '',
        store: '',
        useDeeplinks: useDeeplinks,
        skipUpsell: skipUpsell,
        appType: appType,
        login : login,
        defaults : {productIndex: 0, subfamily: null, host: null, type: null, serial: null},
        productHtml: {__html: ''},
        bannerUrl: '',
        serials : {},
        promoUrl: null
    });

    React.useEffect(() => {
        const doInit = async () => {
            const pageData: any = fetchPageData(props);
            let stateNew;
            if (pageData) {
                const family = await fetchProductFamily(pageData.family)
                const pageDefaults = parseDefaults(pageData.defaults, family);
                stateNew = {
                    ...state,
                    family: family.name,
                    store: pageData.store,
                    defaults: pageDefaults,
                    promoUrl: pageData.promoUrl
                }
            } else {
                const family = await fetchProductFamily(families[0].title);
                const html = {__html: family.descriptionHtml};
                stateNew = {
                    ...state,
                    family: family.name,
                    productHtml : html
                }
            }

            if (login) {
                const serialResponse = await axiosPost('/renewals', login);
                stateNew.serials = compileSerials(serialResponse.data);
            }
            if (isLegacy){
                stateNew.useDeeplinks = false;
            }
            setState(stateNew);
        }
        doInit();
    }, [])

    return (
        <PurchaseContext.Provider value={[state, setState]}>
            {props.children}
        </PurchaseContext.Provider>
    )
}

const fetchPageData = (props: Props) => {
    try{
        const data: any = {
            family: '',
            store: '',
            defaults: {},
        };
        var params = new URLSearchParams(window.location.search);
        const familyName = getFamilyName(props, params);
        data.store = checkStore(props, params);
        data.promoUrl = getPromoUrl(params);
        const productFamily = findFamilyByName(familyName);
        data.family = productFamily.title;
        const collDefaults = productFamily.defaults;
        data.defaults.host = getHost(props, params) || collDefaults.host;
        data.defaults.type = getType(props, params) || collDefaults.type;
        data.defaults.subfamily = getSubFamily(props, params) || collDefaults.subfamily;
        data.defaults.serial = getSerial(props, params);
        const legacySubscriptionTitles = ['Annual Subscription', 'Subscription', 'subscription'];
        if (legacySubscriptionTitles.includes(data.defaults.type)){
            data.defaults.type = 'Subscription';
            data.defaults.subscriptionType = 'New Annual Subscription';
        }
        else if (data.defaults.type == 'Monthly Subscription'){
            data.defaults.type = 'Subscription';
            data.defaults.subscriptionType = 'New Monthly Subscription';
        }
        return data;
    }
    catch(err){
        console.error('Failed to collect data: ' + err.toString());
        return null;
    }
}

const getFamilyName = (props: Props, params: URLSearchParams) => {
    const isLegacy = params.get('collection') != null;
    const familyFieldName = isLegacy ? 'collection' : 'product';
    const appContainer = document.querySelector("div[id^='purchase-app'");
    if (appContainer instanceof HTMLDivElement){
        const defaultCollection = appContainer ? appContainer.dataset.defaultCollection : 'continuum';
        let collectionName = defaultCollection;
        if (props.collection){
            collectionName = props.collection;
        }
        else if (params.get(familyFieldName)){
            collectionName = params.get(familyFieldName);
        }
        return collectionName;
    }
    return ''
}

const checkStore = (props: Props, params: URLSearchParams) => {
    const appContainer = document.querySelector("div[id^='purchase-app'");
    return appContainer instanceof HTMLDivElement ? appContainer.dataset.newDesign : 0;
}

const getSubFamily = (props: Props, params: URLSearchParams) => {
    const isLegacy = params.get('collection') != null;
    if (props.defaultProduct){
        return props.defaultProduct;
    }
    else{
        const subfamilyFieldName = isLegacy ? 'product' : 'subproduct';
        return params.get(subfamilyFieldName);
    }
}

const getHost = (props: Props, params: URLSearchParams) => {
    if (props.defaultHost){
        return props.defaultHost;
    }
    else{
        return params.get('host');
    }
}

const getType = (props: Props, params: URLSearchParams) => {
    if (props.defaultType){
        return props.defaultType;
    }
    else{
        return params.get('purchase-options');
    }
}

const getSerial = (props: Props, params: URLSearchParams) => {
    if (props.defaultAkey){
        return props.defaultAkey;
    }
    else{
        return params.get('akey');
    }
}

const getPromoUrl = (params: URLSearchParams) => {
    return params.get('promo');
}

const findFamilyByName = (name: string) => {
    const slug = slugify(name);
    const family = families.find(item => {
        return slug == item.slug;
    });
    return family;
}

const compileSerials = (sfdcSerialJson: string) => {
    const sfdcSerials = JSON.parse(sfdcSerialJson);
    const result: Record<string, SerialOption> = {};
    sfdcSerials.licenses.forEach((contract: any) => {
        result[contract.serial] = {
            serial: contract.serial,
            product : contract.family,
            host: contract.host,
            qty: contract.quantity,
            endDate: contract.endDate,
            contractType: contract.contractType,
            contractId: contract.contractId,
            isLegacy: contract.isLegacy,
            isFloating: contract.isFloating || false
        };
    });

    sfdcSerials.bundles.forEach((bundle: any) => {
        var serialString = bundle.serials.toString().replace(/,/g, ' + ');
        result[serialString] = {
            serial: serialString,
            product : bundle.family,
            host : bundle.host,
            qty: bundle.quantity,
            endDate : bundle.endDate,
            contractType : bundle.contractType,
            contractId: bundle.contractId,
            isLegacy : bundle.isLegacy,
            isFloating: bundle.isFloating || false
        }
    });
    return result;
}

const parseDefaults = (defaults: any, family: ProductFamily) => {
    const defaultsNew = {...defaults};
    if(!family) return defaultsNew;

    let hostSelection = defaults.host || '';
    family.products.some(p => {
        if (slugify(p.host) == slugify(defaults.host)){
            hostSelection = p.host;
            return true;
        }
    })

    let typeSelection = defaults.type || '';
    family.products.some(p => {
        if (slugify(p.type) == slugify(defaults.type)){
            typeSelection = p.type;
            return true;
        }
    })

    if(typeSelection == 'New Monthly Subscription'){
        defaultsNew.type = 'Subscription';
        defaultsNew.subscriptionType = 'New Monthly Subscription';
    }
    else if (typeSelection == 'New Annual Subscription'){
        defaultsNew.type = 'Subscription';
        defaultsNew.subscriptionType = 'New Annual Subscription';
    }
    else{
        defaultsNew.type = typeSelection;
    }

    return defaultsNew
}

const fetchProductFamily = async (name: string) => {
    let nameKey = name.toLowerCase();
    if (nameKey == 'optics'){
        nameKey = 'optics parent';
    }
    const result = await axiosGet(`/shop/product?name=${nameKey}`);
    return result.data
}

const slugify = (text: string) => {
    return text.toString().toLowerCase()
        .replace(/\s+/g, '-')           // Replace spaces with -
        .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
        .replace(/\-\-+/g, '-')         // Replace multiple - with single -
        .replace(/^-+/, '')             // Trim - from start of text
        .replace(/-+$/, '');            // Trim - from end of text
}

export {PurchaseProvider, PurchaseContext};
